import React, { useState } from 'react';
import {
  Box,
  Button,
  Typography,
  TextField,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  Fade
} from '@mui/material';
import LockResetIcon from '@mui/icons-material/LockReset';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from '../../services/axios';
import ParticlesComponent from './Particles';

const ResetPassword = () => {
  const [params] = useSearchParams();
  const navigate = useNavigate();

  const token = params.get('token');
  const email = params.get('email');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);

  const handleSubmit = async () => {
    setError('');
    setMessage('');

    if (password.length < 6) {
      setError('La contraseña debe tener al menos 6 caracteres');
      return;
    }

    if (password !== confirm) {
      setError('Las contraseñas no coinciden');
      return;
    }

    setLoading(true);

    try {
      const res = await axios.post('/reset-password', {
        token,
        email,
        password,
        password_confirmation: confirm,
      });

      setMessage(res.data.message);
      setTimeout(() => navigate('/login'), 2000);
    } catch (err) {
      setError(err.response?.data?.message || 'Error al cambiar contraseña');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Fade in timeout={800}>
      <Box sx={styles.container}>
        
        {/* Imagen */}
        <Box sx={styles.imageContainer}></Box>

        {/* Partículas */}
        <ParticlesComponent id="tsparticles" style={styles.particles} />

        {/* Formulario */}
        <Box sx={styles.formContainer}>
          <Box sx={styles.form}>

            <LockResetIcon sx={styles.icon} />

            <Typography variant="h4" sx={styles.title}>
              Nueva contraseña
            </Typography>

            <Typography sx={styles.subtitle}>
              Ingresa tu nueva contraseña
            </Typography>

            <TextField
              label="Nueva contraseña"
              type={showPassword ? 'text' : 'password'}
              fullWidth
              margin="normal"
              value={password}
              onChange={e => setPassword(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowPassword(!showPassword)}>
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            <TextField
              label="Confirmar contraseña"
              type={showConfirm ? 'text' : 'password'}
              fullWidth
              margin="normal"
              value={confirm}
              onChange={e => setConfirm(e.target.value)}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton onClick={() => setShowConfirm(!showConfirm)}>
                      {showConfirm ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                )
              }}
            />

            {message && <Alert severity="success" sx={{ mt: 1 }}>{message}</Alert>}
            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}

            <Button
              variant="contained"
              fullWidth
              sx={styles.button}
              onClick={handleSubmit}
              disabled={loading || !password || !confirm}
            >
              {loading ? (
                <CircularProgress size={24} sx={{ color: '#fff' }} />
              ) : (
                'Cambiar contraseña'
              )}
            </Button>

          </Box>
        </Box>
      </Box>
    </Fade>
  );
};

const styles = {
  container: {
    display: 'flex',
    height: '100vh',
  },

  imageContainer: {
    flex: 0.5,
    display: { xs: 'none', md: 'block' },
    backgroundImage: 'url(https://encrypted-tbn0.gstatic.com/images?q=tbn:ANd9GcRbJYJun3zdQESqfgIUZQZEsBnpI5AChIfOKQ&s)',
    backgroundSize: '100% 100%',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center center',
    zIndex: 1200,
  },

  formContainer: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    px: 2,
  },

  form: {
    width: '100%',
    maxWidth: 420,
    padding: { xs: '2rem 1.5rem', md: '2.5rem' },
    borderRadius: '16px',
    backdropFilter: 'blur(15px)',
    background: 'rgba(255,255,255,0.9)',
    boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
    textAlign: 'center',
    zIndex: 1,
  },

  icon: {
    fontSize: 45,
    color: '#2a3b87',
    marginBottom: '10px',
  },

  title: {
    fontWeight: 'bold',
    mb: 1,
  },

  subtitle: {
    color: '#666',
    mb: 2,
    fontSize: { xs: '14px', md: '16px' },
  },

  button: {
    mt: 2,
    py: 1.2,
    fontWeight: 'bold',
    textTransform: 'none',
    background: 'linear-gradient(135deg, #2a3b87, #4e73df)',
    borderRadius: '10px',
    transition: '0.3s',
    '&:hover': {
      background: 'linear-gradient(135deg, #1e2a66, #3b5bcc)',
      transform: 'scale(1.02)',
    },
  },

  particles: {
    position: 'absolute',
    width: '100%',
    height: '100%',
    zIndex: 0,
  },
};

export default ResetPassword;